import { useState, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { styled } from '@mui/material/styles';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@components/Drawer';
import { accountActions, alertActions, googleActions } from '@actions';
import { useTranslation } from '@hooks';
import { handleApiError } from '@helpers/api';
import { companyService } from '@services';
import ImportLoadingOverlay from '@components/ImportLoadingOverlay';
import GoogleLocationsList, { useGoogleLocationsFilter } from '@features/GoogleLocationsList';
import GoogleCompanyCard from './GoogleCompanyCard';

export const Progress = styled(CircularProgress)({
    color: 'var(--text-tertiary)',
    width: '58px !important',
    height: '58px !important',
    display: 'block',
    margin: '0 auto 24px',
});

const ImportGoogleCompanies = ({ isOpen, onClose }) => {
    const [companies, setCompanies] = useState([]);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [progress, setProgress] = useState({ current: 0, total: 0 });

    const account = useSelector(state => state.account?.account);
    const LOCATIONS = useSelector(state => state.google.data);
    const locationsLoading = useSelector(state => state.google.loading);
    const filter = useGoogleLocationsFilter(LOCATIONS, { selected: companies });

    const { t } = useTranslation();
    const dispatch = useDispatch();

    const MAX_AVAILABLE_COMPANIES_COUNT = account?.companiesMaxCount - account?.companies?.length;

    useEffect(() => {
        if (!LOCATIONS.length) {
            dispatch(googleActions.get());
        }
    }, [dispatch, LOCATIONS.length]);

    const onCheckCompany = location => {
        if (companies.some(c => c.locationId === location.locationId)) {
            setCompanies(companies.filter(c => c.locationId !== location.locationId));
            return;
        }
        if (companies.length < MAX_AVAILABLE_COMPANIES_COUNT) {
            setCompanies([...new Set([...companies, location])]);
        }
    };

    const handleSubmit = async () => {
        setIsSubmitting(true);
        setProgress({ current: 0, total: companies.length });
        try {
            const { imported, skipped, failed } = await companyService.importGoogleCompaniesWithProgress(
                companies,
                (current, total) => setProgress({ current, total }),
            );
            await dispatch(accountActions.get());
            if (failed.length) {
                handleApiError({ errorCode: failed[0].reason }, 'alertMessages.importFail');
            } else if (skipped.length) {
                dispatch(alertActions.success(t('alertMessages.importPartial', { imported, skipped: skipped.length })));
            } else {
                dispatch(alertActions.success(t('alertMessages.importSuccess')));
            }
        } catch (error) {
            handleApiError(error, 'alertMessages.importFail');
        } finally {
            onClose();
            dispatch(googleActions.reset());
            setIsSubmitting(false);
        }
    };

    return (
        <>
            <ImportLoadingOverlay open={isSubmitting} current={progress.current} total={progress.total} />
            <Drawer
                anchor="right"
                open={isOpen}
                onClose={onClose}
                title={t('SettingsCompanies.importCompanies')}
                subtitle={t('SettingsCompanies.selectCompanies')}
                primaryText={
                    companies.length
                        ? t('SettingsCompanies.importCompaniesCount', { count: companies.length })
                        : t('SettingsCompanies.importCompanies')
                }
                primaryDisabled={!companies.length || isSubmitting}
                primaryAction={handleSubmit}
            >
                {!locationsLoading ? (
                    <GoogleLocationsList
                        filter={filter}
                        renderLocation={l => (
                            <GoogleCompanyCard location={l} selectedCompanies={companies} setCompany={onCheckCompany} />
                        )}
                    />
                ) : (
                    <Progress />
                )}
            </Drawer>
        </>
    );
};

export default ImportGoogleCompanies;
